/**
 * iDecide - Logo Reveal plugin
 * Add the panel options for logo reveal elements, and create HTML overlay logo reveal elements from here.
 */

import CreativeEditorSDK, {type AssetResult} from '@cesdk/cesdk-js';
import type {CreativeEngine} from '@cesdk/cesdk-js';
import {
    BLOCK_TYPE,
    PRESENTATION_WIDTH,
    RUB_SCREEN_META_KEY,
    RUB_YOUR_SCREEN_COVER_JS_URL,
    RUB_YOUR_SCREEN_COVER_CSS_URL,
    RUB_YOUR_SCREEN_COVER_IMAGE_URL,
    RUB_YOUR_SCREEN_ICON_URL,
    RUB_YOUR_SCREEN_AUDIO_URL
} from "../constants.ts";
import {
    isEngineReady, loadScriptOnce
} from "../utils/utilities.ts";
import {positionOverlayToBlock, updateOverlayTimelineVisibility} from "../utils/interactionOverlay.ts";
import {findOwningPage} from "../utils/slideUtilities.ts";


const RUB_YOUR_SCREEN_SOURCE_ID = 'idecide.logo_reveal.rub_your_screen.source';
export const LOGO_REVEAL_ENTRY_ID = 'idecide.logo_reveal';

/**
 * 'Rub Your Screen' interaction options.
 */
export type RubMeta = {
    id: string;
    elementType: string;
    autoAdvanceSlideId: null,
    pauseWhileActive?: boolean;        // default true
    text?: string;                     // default "Rub Your Screen"
    style?: {
        handColor?: string;
    };
    images?: {
        coverImageUrl?: string;        // Custom background image
        coverIconUrl?: string;         // Custom icon image
    }
};

export function defaultStyle(): Required<NonNullable<RubMeta['style']>> {
    return {
        handColor: 'blue',
    };
}

export function withDefaults(meta: Partial<RubMeta>): RubMeta {
    const m: RubMeta = {
        id: meta.id || `logo-reveal-rub-your-screen-${Math.random().toString(36).slice(2, 8)}`,
        elementType: RUB_SCREEN_META_KEY,
        autoAdvanceSlideId: meta.autoAdvanceSlideId || null,
        pauseWhileActive: meta.pauseWhileActive ?? true,
        text: meta.text || "Rub Your Screen",
        style: {...defaultStyle(), ...(meta.style ?? {})},
        images: {
            coverImageUrl: meta.images?.coverImageUrl,
            coverIconUrl: meta.images?.coverIconUrl
        }
    }

    return m;
}

// Keep a single registry so overlays can be replaced in-place without flicker.
const LOGO_REVEAL_OVERLAYS = new Map<number, HTMLDivElement>();

// Track metadata for each overlay to detect changes and rebuild
const LOGO_REVEAL_METADATA_CACHE = new Map<number, string>();

// Track overlay visibility state per block (for builder toggle functionality)
const OVERLAY_VISIBILITY_MAP = new Map<number, boolean>();

//SVG used for the Rub Your Screen Asset Library card
const RUB_YOUR_SCREEN_SVG = encodeURIComponent(`
<svg viewBox="0 0 258 214" xmlns="http://www.w3.org/2000/svg"><defs xmlns="http://www.w3.org/2000/svg"><linearGradient id="a" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stop-color="#0989cf"/><stop offset="100%" stop-color="#1f61c1"/></linearGradient></defs><path d="M240 158c0-2.8-.2-5.8-.6-8.9-.5-3.1-1.3-6.4-2.4-10.1-.2-.7-.5-1.3-.7-2-.1-.7-.3-1.4-.5-2l-.1-.2c-1-3.6-2.2-7.3-3.4-11-.3-1.2-.7-2.4-1.1-3.6-.9-2.5-1.9-4.9-3.1-7.1-2.4-4.9-5.8-8.5-10.2-10.8-4.5-2.4-9-3.1-13.7-2.1-.7.2-1.3.3-2 .1s-1.2-.4-1.6-.7l-.8-.4c-.2-.2-.5-.4-.9-.6-3.8-2.6-7.5-3.3-11.2-1.9-3.8 1.4-6.3 2.4-7.4 3-.4 0-.7 0-.9.1-1.8-2.2-3.6-3.4-5.4-3.6-1.1-.1-2.2-.1-3.2.1-1.5-1.2-3.6-1.6-5.5-1-.5.1-1 .3-1.5.5-1.5-4.2-2.8-7.7-3.8-10.4 1.2-1.8 1.9-4.1 1.7-6.4-.1-.7.9-1.6 1.6-2.2 2.3-2 4.8-3.7 6.9-5.9 2.6-2.4 3.1-6.3 1.4-9.3-1.8-3.1-5.6-4.3-8.9-2.9-4.2 1.4-8.3 3-12.2 5-1.3-1.7-2.7-2.8-4.2-3.3-1.1-.4-2.2-.7-3.1-.9 1.8-1.5 3.6-3 5.4-4.4 6.2-5.1 13-9.5 19.4-14.5 3.2-1.8 4.3-5.9 2.4-9.1-2.3-2.8-6.3-3.6-9.4-1.7l-.9.4c-2.5 1.4-5.1 2.7-7.6 4.2-4.4 2.6-8.7 5.2-13 7.8l-.4-.6c1.5-1.1 3.1-2.2 4.6-3.3 4.1-3 8.3-6 12.3-9.1 2.6-1.8 3.5-5.3 2.2-8.2-.2-1-.1-2.1.5-3 .3-.3.5-.7.6-1.1 1.4-3 .1-6.6-2.9-8-2.9-2-6.8-1.6-9.4.8-.6.5-1.3 1-2 1.4-9.8 5.9-19.7 11.7-29.4 17.6-4 2.5-7.9 5.1-11.9 7.7l-.4-.5c.6-.5 1.1-1.1 1.7-1.5 7.1-5.3 14.1-10.5 21.2-15.8.9-.6 1.7-1.3 2.4-2.1 1.2-1.2 1.4-3.1.4-4.6s-2.9-2.1-4.6-1.4c-1.5.6-3 1.2-4.5 2-14.1 6.8-27.5 15.5-39.7 25.7-8 6.5-16.2 13-23.8 20-5.6 5.1-10.3 11-15.6 16.4-.3.2-.5.4-.8.6-3.6 3.1-4 8.5-.9 12-2.3 1.8-4.7 3.7-7.1 5.5-2.7 1.8-4.7 4.4-5.8 7.4-.1.2-.1.3-.2.5-1.3 3.7.6 7.7 4.2 9.1 1.3.6 1.8 1.2 1.6 2.8-.2.8-.2 1.7-.1 2.6.6 3.5 3.9 5.9 7.4 5.3 2.3-.4 4.5-1 6.6-1.8 1.1-.4 2.1-.9 3.2-1.4-.1.8-.5 1.5-1.1 2.1-6.6 6.5-13.2 13.1-19.8 19.7-3.6 3.3-7.1 6.9-10.3 10.6-.7.9-1.2 2-1.4 3.2-.6 3.8 2 7.3 5.8 7.9 1.1.3 2.2.9 2.9 1.9 1.7 2.8 5.1 4.1 8.2 3.1 3.9-1.4 7.7-3.1 11.4-5.2 4.4-2.4 8.5-5.4 12.7-8 .7-.3 1.6-.2 2.2.3 1.9 2.7 5.3 4 8.5 3.2.7-.1 1.5 0 2.5 0l-.6 1.3c-.8 1.1-1.2 2.5-1.2 3.9.1 3.5 3.1 6.2 6.6 6.1 2.5-.1 5-.6 7.4-1.3 1.6-.5 3.2-1.1 4.7-1.8-1 1.4-2.2 2.7-3.5 3.9-1.9 1.7-2.5 4.4-1.7 6.8.7 2.5 2.9 4.2 5.5 4.4 1.9.3 3.9.2 5.8-.3 6.6-2.2 13.2-4.7 19.7-7.2 1.3-.5 2.1-.6 3.2.7 1.9 2.8 5.5 3.8 8.6 2.4 1.6-.5 3.2-1.2 4.7-1.8 0 .1.1.2.1.3.9 2.5 2 4.8 3.3 7.1 4.1 7.6 10 14.1 17.5 19.7 6.2 4.6 12.6 7.6 19.5 9.1 6.7 1.6 13.6 1.5 20.8-.1 3.2-.6 6.4-1.5 9.5-2.6 1.2-.4 2.3-.9 3.5-1.5 4.4-1.9 8.8-4.3 13.2-7.1 6.7-4.1 11.9-8.9 15.7-14.5 3.7-5.7 6.1-12 7.1-18.9.2-.8.2-1.7.1-2.7-.1-1.1 0-2.4 0-3.9zM105.4 92c-1.8 1.2-3.5 2.4-5.3 3.6.1-.6.4-1.2.9-1.6 5.9-4.9 11.7-9.8 17.6-14.6v.2c.1.9.3 1.8.5 2.7-4.6 3.2-9.2 6.4-13.7 9.7zm17.2 23l-.3-.5c.9-.8 1.8-1.6 2.8-2.3l3.6-2.7c.2.6.5 1.2.7 1.8l.3.8c-2.3 1.1-4.7 1.9-7.1 2.9zm9.7 21.1c-.7 1.1-1.5 2.3-2.2 3.7.6-1.5 1.4-2.7 2.2-3.7zm2.3-2.9c-.1 0 0 0 0 0zm92.8 41.5c-2.9 6.3-8 11.8-15.4 16.4-2.7 1.6-5.2 3-7.6 4.2-2.5 1.4-4.9 2.5-7.3 3.3-1.4.5-2.9 1-4.4 1.4s-3 .7-4.5 1c-5.3.9-10.5.8-15.5-.3s-9.9-3.3-14.7-6.4c-6.4-4.4-11.4-9.6-15-15.7-1.8-3-3.2-6.3-4.3-9.8s-1.9-7.2-2.3-11.2c-.2-2.2-.1-4.3.3-6.4 0-.1 0-.2.1-.3.6-2.8 2.2-5.3 4.4-7.3 1 3.6 6.9 18.3 6.6 17.3.4 1.1 1 1.9 1.8 2.4l.6.3c1.3.6 2.5.6 3.9.1 1.2-.4 2.1-1.3 2.6-2.5.3-.6.5-1.1.5-1.7s-.1-1.3-.3-1.9c-3.2-8.7-5.6-15.2-7.3-19.7-1.1-2.9-1.9-5-2.4-6.5-.7-2-1.5-3.9-2.2-5.9s-1.5-3.9-2.2-5.9c-1.5-4-2.9-8-4.4-12-.5-1.4-1.1-2.9-1.6-4.3-.1-.3-.2-.5-.3-.8-2.1-5.8-4.3-11.5-6.4-17.3-.4-1.2-.8-2.5-1-3.9-.2-1.3-.3-2.6-.3-3.9 0-.5.1-1 .2-1.5.1-.6.3-1.3.6-1.9.6-1.3 1.3-2.3 2.2-3.1 1.2-.9 2.4-1.5 3.8-1.8 1.2-.3 2.5-.4 3.9-.3.3.1.5.1.7.2 1.1.4 2 .9 2.6 1.7.9.9 1.6 1.8 2.1 2.8s1 2 1.4 3.1c.6 1.1 1.1 2.3 1.5 3.5 1.4 3.9 2.9 7.8 4.3 11.6l.3.9 2.4 6.6c.8 2.1 1.6 4.2 2.4 6.4 1.4 3.8 2.4 6.6 3.1 8.5.8-1.3 1.8-2.4 2.9-3.3.4-.3.9-.7 1.3-1 1.6-1 3.5-1.7 5.7-2.1h.3c2-.3 3.9-.1 5.7.5 1.9.8 3.7 1.9 5.3 3.4 1.3-1.2 2.8-2.3 4.4-3.2.7-.4 1.5-.8 2.3-1 1.1-.3 2.3-.4 3.5-.3 2.2.2 3.9 1 5.3 2.3s2.6 2.7 3.8 4.2c.1 0 .2-.2.3-.3.3-.1.4-.2.5-.4.3-.3.8-.6 1.2-.9.3-.3.8-.5 1.3-.7 2.8-1.3 5.6-1.5 8.3-.4 2.6 1 4.7 3 6.2 6 1.1 2.3 2.1 4.4 3 6.3.7 2 1.4 3.9 1.9 5.8.2.5.4 1.1.7 1.8.2.7.4 1.3.5 1.8 1.1 3.3 2 6.8 2.9 10.3 1 3.5 1.8 7 2.3 10.5 1.5 8 .6 15.1-2.5 21.3z" fill="url(#a)"/></svg>
`);

export function getLogoRevealMeta(engine: CreativeEngine, blockId: number): RubMeta {
    try {
        const raw = (engine.block as any).getMetadata?.(blockId, RUB_SCREEN_META_KEY);
        if (raw) return withDefaults(JSON.parse(raw));
    } catch {
    }
    return withDefaults({id: `logo-reveal-${blockId}`});
}

export function installLogoRevealPlugin(
    cesdk: CreativeEditorSDK,
    options: { mountInteractiveOverlay?: boolean; autoOpenLibrary?: boolean } = {}
): { updateForActivePage: (pageId: number) => void; cleanup: () => void } {
    console.log('installLogoRevealPlugin');
    // Check if engine is ready before attempting to install
    if (!cesdk?.engine?.editor || !cesdk?.engine?.asset || !cesdk?.engine?.block) {
        console.warn('[LogoReveal] Engine not ready, returning no-op controller');
        return {
            updateForActivePage: () => {},
            cleanup: () => {}
        };
    }

    cesdk.engine.editor.setSettingBool('controlGizmo/showMoveHandles', true);
    addRubYourScreenAssetSource(cesdk);

    // Asset Library entry
    cesdk.ui.addAssetLibraryEntry({
        id: LOGO_REVEAL_ENTRY_ID,
        sourceIds: [RUB_YOUR_SCREEN_SOURCE_ID],
        title: () => 'Logo Reveals',
        previewLength: 2,
        gridColumns: 2,
        gridItemHeight: 'square',
        cardBorder: false,
        cardLabel: () => 'Rub Your Screen',
        cardBackgroundPreferences: [{type: 'image' as any, path: 'meta.thumbUri'}],
        previewBackgroundType: 'cover',
    });

    // Listen for when Logo Reveal blocks are selected and restore their position/size
    // This prevents them from being moved or resized
    installLogoRevealProtection(cesdk.engine);

    // Register canvas menu button for toggling overlay visibility in builder
    installLogoRevealCanvasMenu(cesdk);

    // DOM overlays
    if (options.mountInteractiveOverlay !== false) {
        const engine = cesdk.engine;
        let overlayContainer = document.getElementById('overlay-container');
        if (!overlayContainer) {
            const host = document.getElementById("root-shadow");
            const root = host?.shadowRoot || (host as any)?.__shadowRoot;
            overlayContainer = root?.getElementById('overlay-container');
        }
        return mountLogoRevealOverlays(engine, {
            container: overlayContainer,
            interactive: false,
            clearHTMLOverlays: true  // Clear any stale overlays from previous page
        });
    }

    // Return no-op controller if overlays are disabled
    return {
        updateForActivePage: () => {},
        cleanup: () => {}
    };
}

/**
 * Install protection that prevents Logo Reveal blocks from being moved or resized
 * Also handles duration updates to ensure page duration matches the longest block
 */
function installLogoRevealProtection(engine: CreativeEngine): void {
    const originalPositions = new Map<number, { x: number; y: number; w: number; h: number }>();
    const originalDurations = new Map<number, number>();
    const trackedLogoReveals = new Set<number>();

    // Find all existing logo reveals and initialize their positions
    const initializeLogoReveals = () => {
        const logoReveals = findAllLogoReveals(engine);

        logoReveals.forEach((blockId) => {
            if (!trackedLogoReveals.has(blockId)) {
                const parent = engine.block.getParent?.(blockId);
                if (parent) {
                    const pageW = engine.block.getWidth?.(parent) ?? 1920;
                    const pageH = engine.block.getHeight?.(parent) ?? 1080;
                    originalPositions.set(blockId, { x: 0, y: 0, w: pageW, h: pageH });
                    trackedLogoReveals.add(blockId);
                }
            }
        });

        return logoReveals;
    };

    // Initial setup
    const logoReveals = initializeLogoReveals();

    // Function to restore position/size if changed
    const restoreLogoRevealTransform = (blockId: number) => {
        try {
            const current = originalPositions.get(blockId);
            if (!current) return;

            const currentX = engine.block.getPositionX?.(blockId) ?? 0;
            const currentY = engine.block.getPositionY?.(blockId) ?? 0;
            const currentW = engine.block.getWidth?.(blockId) ?? current.w;
            const currentH = engine.block.getHeight?.(blockId) ?? current.h;

            // If position or size has changed, restore it
            if (
                Math.abs(currentX - current.x) > 0.1 ||
                Math.abs(currentY - current.y) > 0.1 ||
                Math.abs(currentW - current.w) > 0.1 ||
                Math.abs(currentH - current.h) > 0.1
            ) {

                engine.block.setPositionX?.(blockId, current.x);
                engine.block.setPositionY?.(blockId, current.y);
                engine.block.setWidthMode?.(blockId, 'Absolute');
                engine.block.setHeightMode?.(blockId, 'Absolute');
                engine.block.setWidth?.(blockId, current.w);
                engine.block.setHeight?.(blockId, current.h);
            }
        } catch (e) {
            // Block might have been deleted
        }
    };

    // Subscribe ONLY to logo reveal blocks (huge performance improvement!)
    if (logoReveals.length > 0) {
        engine.event?.subscribe?.(logoReveals, (events) => {
            events.forEach((event) => {
                if (event.type === 'Updated') {
                    restoreLogoRevealTransform(event.block);
                }
            });
        });
    }

    // Also subscribe to scene-level events to detect new logo reveals being added
    const sceneId = engine.scene?.get?.() ?? 1;
    engine.event?.subscribe?.([sceneId], (events) => {
        events.forEach((event) => {
            if (event.type === 'Created') {
                // Check if newly created blocks are logo reveals
                const newLogoReveals = initializeLogoReveals();

                // If we found new logo reveals, we need to re-subscribe
                // For now, just initialize their positions - they'll be protected on next update
                newLogoReveals.forEach((blockId) => {
                    if (!trackedLogoReveals.has(blockId)) {
                        // New logo reveal detected - could re-subscribe here if needed
                        // For simplicity, we'll just track them
                        trackedLogoReveals.add(blockId);
                    }
                });
            }
        });
    });
}

/**
 * Register a canvas menu button to toggle logo reveal overlay visibility in the builder
 */
function installLogoRevealCanvasMenu(cesdk: CreativeEditorSDK): void {
    cesdk.ui.registerComponent(
        'toggleLogoRevealOverlay.canvasMenu',
        ({builder: {Button}, engine}) => {
            Button('toggle-logo-reveal-overlay', {
                label: 'Toggle Overlay Visibility',
                onClick: () => {
                    const selectedBlocks = engine.block.findAllSelected();
                    if (selectedBlocks?.length === 1) {
                        const blockId = selectedBlocks[0];

                        // Check if this is a logo reveal block
                        try {
                            const meta = engine.block.getMetadata(blockId, RUB_SCREEN_META_KEY);
                            if (!meta) return; // Not a logo reveal block
                        } catch {
                            return;
                        }

                        // Find the overlay element
                        let overlayContainer = document.getElementById('overlay-container');
                        if (!overlayContainer) {
                            const host = document.getElementById("root-shadow");
                            const root = host?.shadowRoot || (host as any)?.__shadowRoot;
                            overlayContainer = root?.getElementById('overlay-container');
                        }
                        if (!overlayContainer) return;

                        const overlay = overlayContainer.querySelector(`[data-block-id="${blockId}"]`) as HTMLElement;
                        if (!overlay) return;

                        // Get the content element (not the root, which has the toggle button)
                        const content = overlay.querySelector('[data-role="content"]') as HTMLElement;
                        if (!content) return;

                        // Toggle visibility
                        const currentVisibility = OVERLAY_VISIBILITY_MAP.get(blockId) ?? true;
                        const newVisibility = !currentVisibility;
                        OVERLAY_VISIBILITY_MAP.set(blockId, newVisibility);

                        content.style.display = newVisibility ? 'block' : 'none';
                    }
                }
            });
        }
    );

    // Install selection handler to show/hide canvas menu button
    installLogoRevealSelectionHandler(cesdk);
}

/**
 * Install selection handler to show/hide the canvas menu toggle button
 * when a logo reveal block is selected
 */
function installLogoRevealSelectionHandler(cesdk: CreativeEditorSDK): void {
    cesdk.engine.block.onSelectionChanged(() => {
        const selectedBlocks = cesdk.engine.block.findAllSelected();
        const currentOrder = cesdk.ui.getCanvasMenuOrder() || [];
        const hasToggleButton = currentOrder.some((item: any) => item?.id === 'toggleLogoRevealOverlay.canvasMenu');

        if (selectedBlocks?.length === 1) {
            const blockId = selectedBlocks[0];

            // Check if selected block is a logo reveal
            try {
                const meta = cesdk.engine.block.getMetadata?.(blockId, RUB_SCREEN_META_KEY);

                if (meta && !hasToggleButton) {
                    // Add button to canvas menu
                    cesdk.ui.setCanvasMenuOrder([
                        'toggleLogoRevealOverlay.canvasMenu',
                        ...currentOrder
                    ]);
                } else if (!meta && hasToggleButton) {
                    // Remove button from canvas menu
                    const cleanedOrder = currentOrder.filter((item: any) => item?.id !== 'toggleLogoRevealOverlay.canvasMenu');
                    cesdk.ui.setCanvasMenuOrder(cleanedOrder);
                }
            } catch {
                // Not a logo reveal block
                if (hasToggleButton) {
                    const cleanedOrder = currentOrder.filter((item: any) => item?.id !== 'toggleLogoRevealOverlay.canvasMenu');
                    cesdk.ui.setCanvasMenuOrder(cleanedOrder);
                }
            }
        } else if (hasToggleButton) {
            // Remove button when no single block is selected
            const cleanedOrder = currentOrder.filter((item: any) => item?.id !== 'toggleLogoRevealOverlay.canvasMenu');
            cesdk.ui.setCanvasMenuOrder(cleanedOrder);
        }
    });
}

function addRubYourScreenAssetSource(cesdk: CreativeEditorSDK): void {
    const engine = cesdk.engine as CreativeEngine;

    const source: any = {
        id: RUB_YOUR_SCREEN_SOURCE_ID,
        findAssets: async (queryData: any) => ({
            assets: [
                {
                    id: 'idecide.logo_reveal.rub_your_screen',
                    locale: 'en',
                    label: 'Rub Your Screen',
                    tags: ['logo reveal'],
                    meta: {
                        thumbUri: `data:image/svg+xml;utf8,${RUB_YOUR_SCREEN_SVG}`,
                        blockType: '//ly.img.ubq/graphic',
                        width: 800,
                        height: 64,
                    },
                    context: {sourceId: RUB_YOUR_SCREEN_SOURCE_ID},
                },
            ],
            total: 2,
            currentPage: queryData?.page ?? 0,
            nextPage: undefined
        }),

        /** Create and return a logo reveal block. */
        applyAsset: async (_assetResult: AssetResult) => {
            const page = engine.scene.getCurrentPage?.();
            if (page) {
                const block = createLogoRevealBlock(engine, page, undefined);
                return block;
            }
        }
    };

    try {
        const existing: any = (engine as any).asset?.findAllSources?.();
        if (!existing || (Array.isArray(existing) && !existing.includes(RUB_YOUR_SCREEN_SOURCE_ID))) {
            engine.asset.addSource(source);
        }
    } catch (e) {
        console.error('Error adding logo reveal asset source.', e);
    }
}

export function createLogoRevealBlock(
    engine: CreativeEngine,
    parentPage: number,
    meta?: Partial<RubMeta>
): number {
    // Stable id for this element
    const id = `logo-reveal-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    const logoReveal: RubMeta = withDefaults({id, ...meta});

    // Create the underlying graphic block
    const block = engine.block.create('graphic');

    setRubMeta(engine, block, logoReveal);

    // Name in layer panel
    engine.block.setName?.(block, 'Logo Reveal');

    // Always full size of the page
    const pageW = engine.block.getWidth?.(parentPage) ?? 1920;
    const pageH = engine.block.getHeight?.(parentPage) ?? 1080;

    engine.block.setWidthMode?.(block, 'Absolute');
    engine.block.setHeightMode?.(block, 'Absolute');
    engine.block.setWidth?.(block, pageW);
    engine.block.setHeight?.(block, pageH);
    engine.block.setPositionX?.(block, 0);
    engine.block.setPositionY?.(block, 0);

    try {
        const shape = engine.block.createShape?.('rect');
        engine.block.setShape?.(block, shape);
    } catch {
        // Shape creation failed, continue anyway
    }

    //Set duration of block to go the end of the slide
    const parentDuration = engine.block.getDuration(parentPage);
    //Only set the duration if another block has increased the maximum duration of the slide, else it will be set to 0
    if (parentDuration > 0){
        engine.block.setDuration(block, parentDuration);
    } else {
        // If no duration is set, set a default duration that can be resized
        engine.block.setDuration(block, 5.0); // 5 seconds default
    }

    // Attach to page & mark type
    engine.block.appendChild(parentPage, block);
    engine.block.setMetadata(block, BLOCK_TYPE, RUB_SCREEN_META_KEY);

    // Make block non-resizable and non-draggable (MUST be done AFTER appendChild)
    try {
        // Method 1: Try using scope API
        if (typeof engine.block.setScopeEnabled === 'function') {
            // Allow timeline resizing but prevent position/size changes
            engine.block.setScopeEnabled(block, 'layer/resize', false);
            engine.block.setScopeEnabled(block, 'layer/move', false);
            engine.block.setScopeEnabled(block, 'layer/rotate', false);
            engine.block.setScopeEnabled(block, 'layer/flip', false);
            engine.block.setScopeEnabled(block, 'layer/crop', false);
            engine.block.setScopeEnabled(block, 'layer/rotate', false);
        }

        // Method 2: Try using the fill API to lock dimensions
        if (typeof engine.block.setContentFillMode === 'function') {
            try {
                engine.block.setContentFillMode(block, 'Contain');
            } catch {}
        }

        // Method 3: Set the block as clipped to prevent interactions
        if (typeof engine.block.setClipped === 'function') {
            try {
                engine.block.setClipped(block, false);
            } catch {}
        }
    } catch (e) {
        console.warn('Could not disable resize/move for logo reveal block:', e);
    }

    return block;
}

export function safeHasLogoRevealMeta(engine: CreativeEngine, id: number): boolean {
    try {
        const raw = (engine.block as any).getMetadata?.(id, RUB_SCREEN_META_KEY);
        if (!raw) return false;
        try {
            JSON.parse(raw);
        } catch {
            return false;
        }
        return true;
    } catch {
        return false;
    }
}

/** Find every block in the scene that carries logo-reveal meta (depth-first)*/
export function findAllLogoReveals(engine: any): number[] {
    const pages: number[] = engine.scene?.getPages?.() ?? [];
    const out: number[] = [];
    const stack: number[] = [...pages];
    while (stack.length) {
        const id = stack.pop()!;
        const children: number[] = engine.block.getChildren?.(id) ?? [];
        for (const c of children) stack.push(c);
        const hasMetadata = safeHasLogoRevealMeta(engine, id);
        if (hasMetadata) {
            out.push(id);
        }
    }
    return out;
}

/** --------------------------------------
 * Overlays – DOM-based logo reveal UI
 * ------------------------------------- */
export function mountLogoRevealOverlays(
    engine: any,
    opts: {
        container?: HTMLElement | null;
        interactive?: boolean;
        clearHTMLOverlays?: boolean;
        onComplete?: (blockId: number) => void;
    } = {}
): { updateForActivePage: (pageId: number) => void; cleanup: () => void } {
    const host = opts.container ?? (engine as any).element?.parentElement ?? document.body;
    let clearOverlays = opts.clearHTMLOverlays;

    const overlays = LOGO_REVEAL_OVERLAYS;

    const rebuild = () => {
        const all: number[] = findAllLogoReveals(engine);

        if (clearOverlays) {
            overlays.forEach((el) => {
                try {
                    (el as any).__teardown__?.();
                } catch {
                }
                try {
                    el.remove();
                } catch {
                }
            });
            overlays.clear();
            OVERLAY_VISIBILITY_MAP.clear(); // Clear all visibility states
            clearOverlays = false;
        } else {
            // remove stale (block gone or meta removed)
            [...overlays.keys()].forEach((id) => {
                if (!all.includes(id)) {
                    try {
                        (overlays.get(id) as any).__teardown__?.();
                    } catch {
                    }
                    overlays.get(id)?.remove();
                    overlays.delete(id);
                    OVERLAY_VISIBILITY_MAP.delete(id); // Clean up visibility state
                }
            });
        }

        // add fresh or rebuild if metadata changed
        all.forEach((id) => {
            // Get current metadata
            const currentMeta = JSON.stringify(getLogoRevealMeta(engine, id));
            const cachedMeta = LOGO_REVEAL_METADATA_CACHE.get(id);

            // Check if overlay exists and metadata has changed
            const needsRebuild = overlays.has(id) && cachedMeta !== currentMeta;

            if (needsRebuild) {
                // Remove existing overlay to rebuild with new metadata
                try {
                    (overlays.get(id) as any).__teardown__?.();
                } catch {}
                overlays.get(id)?.remove();
                overlays.delete(id);
                LOGO_REVEAL_METADATA_CACHE.delete(id);
            }

            if (!overlays.has(id)) {
                // Store metadata for change detection
                LOGO_REVEAL_METADATA_CACHE.set(id, currentMeta);

                const el = createLogoRevealOverlay(engine, id, host, !!opts.interactive, {
                    onComplete: opts.onComplete
                });
                overlays.set(id, el);
                // Position overlay first, then initialize scratch-off
                requestAnimationFrame(() => {
                    positionOverlayToBlock(engine, id, el, updateLogoRevealGeometry);

                    // Use double RAF to ensure layout has been applied
                    requestAnimationFrame(() => {
                        const content = el.querySelector('[data-role="content"]') as HTMLElement;
                        // Query for elements with unique IDs based on blockId
                        const uniqueId = `logo-reveal-${id}`;
                        const coverLayers = el.querySelector(`#${uniqueId}-coverLayers`) as HTMLElement;
                        const coverCanvas = el.querySelector(`#${uniqueId}-coverCanvas`) as HTMLElement;
                        const scratchAudio = el.querySelector(`#${uniqueId}-scratchAudio`) as HTMLElement;
                        if (content && coverLayers && coverCanvas && scratchAudio) {
                            // Apply stored visibility state (from toggle button in builder)
                            const storedVisibility = OVERLAY_VISIBILITY_MAP.get(id) ?? true;
                            content.style.display = storedVisibility ? 'block' : 'none';

                            // Store initialization width for later scaling
                            const initWidth = content.offsetWidth || PRESENTATION_WIDTH;
                            el.dataset.initWidth = String(initWidth);

                            void bootCanvasScratchOff({
                                containerEl: content,  // Use content element as container (has proper dimensions)
                                coverLayers,
                                coverCanvas,
                                scratchAudio,
                                engine,
                                blockId: id
                            }).catch(err => {
                                console.error('Canvas scratch-off boot failed:', err);
                            });
                        }
                    });
                });
            }
        });
    };

    const layoutAll = () => overlays.forEach((el, id) => positionOverlayToBlock(engine, id, el, updateLogoRevealGeometry));

    let scheduled = false;
    const schedule = () => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
            scheduled = false;
            rebuild();
            layoutAll();
            // Update visibility after rebuild to handle slow timeline scrubbing
            // (onStateChanged doesn't fire continuously during slow drags)
            updateOverlayTimelineVisibility(engine, LOGO_REVEAL_OVERLAYS, isEngineReady);
        });
    };

    // initial pass
    schedule();

    // Track current logo reveal subscriptions
    let currentLogoReveals: number[] = [];
    let unsubLogoReveals: (() => void) | null = null;

    // Track state changed subscription - only subscribe when there are logo reveals
    let unsubStateChanged: (() => void) | null = null;
    let visibilityRafId: number | null = null;

    // Function to update logo reveal subscriptions dynamically
    const updateLogoRevealSubscriptions = () => {
        const logoReveals = findAllLogoReveals(engine);

        // Only re-subscribe if the list of logo reveals changed
        if (JSON.stringify(logoReveals) !== JSON.stringify(currentLogoReveals)) {
            // Unsubscribe from old logo reveals
            if (unsubLogoReveals) {
                try {
                    unsubLogoReveals();
                } catch {}
            }

            // Unsubscribe from state changes if we had a subscription
            if (unsubStateChanged) {
                try {
                    unsubStateChanged();
                    unsubStateChanged = null;
                } catch {}
            }
            if (visibilityRafId !== null) {
                cancelAnimationFrame(visibilityRafId);
                visibilityRafId = null;
            }

            // Subscribe to new logo reveals for position/metadata updates
            currentLogoReveals = logoReveals;
            unsubLogoReveals = logoReveals.length > 0
                ? engine.event?.subscribe?.(logoReveals, (events: any) => {
                    try {
                        // Filter out events for invalid/deleted blocks using same validation as hardenTimelineApi
                        const validEvents = events.filter((e: any) => {
                            try {
                                const id = e.block;
                                // Check if ID is a valid positive integer before calling engine methods
                                if (!Number.isInteger(id) || id <= 0 || id === 4294967295) {
                                    return false;
                                }
                                return engine.block.isValid?.(id);
                            } catch {
                                return false;
                            }
                        });

                        // Only rebuild/layout if logo reveals were actually updated
                        const hasRelevantEvent = validEvents.some((e: any) => e.type === 'Updated');
                        if (hasRelevantEvent) {
                            schedule();
                        }
                    } catch (error) {
                        // Ignore errors from invalid blocks
                        console.warn('Logo reveal event handler error:', error);
                    }
                })
                : null;

            // Watch for playback time changes ONLY if there are logo reveals
            // This is much more efficient than onStateChanged which fires constantly
            if (logoReveals.length > 0) {
                // Try to find the playhead element in Shadow DOM (Builder mode)
                const rootEl = document.getElementById('root-shadow');
                const shadowRoot = rootEl?.shadowRoot;
                const playheadElement = shadowRoot?.querySelector('[role="slider"][aria-valuenow]');

                // Fallback: Try to find the review wrapper with data-playback-time (Review/Player mode)
                const reviewWrapper = document.querySelector('.review-cesdk-wrapper[data-playback-time]');

                const timeElement = playheadElement || reviewWrapper;

                if (timeElement) {
                    const timeAttribute = playheadElement ? 'aria-valuenow' : 'data-playback-time';

                    // Track last processed rounded time to only update on 10th-of-second changes
                    let lastProcessedTime = -1;

                    const timeObserver = new MutationObserver((mutations) => {
                        // Check if the time attribute changed
                        const timeChanged = mutations.some(mutation =>
                            mutation.type === 'attributes' && mutation.attributeName === timeAttribute
                        );

                        if (timeChanged) {
                            const currentTimeValue = timeElement.getAttribute(timeAttribute);
                            if (!currentTimeValue) return;

                            // Parse and round to 1 decimal place (10ths of a second)
                            const parsedTime = parseFloat(currentTimeValue);
                            const roundedTime = Math.round(parsedTime * 10) / 10;

                            // Only process if the rounded time actually changed
                            if (roundedTime === lastProcessedTime) {
                                return; // Skip this update, still on same 10th of second
                            }

                            lastProcessedTime = roundedTime;

                            // Cancel any pending visibility update
                            if (visibilityRafId !== null) {
                                cancelAnimationFrame(visibilityRafId);
                            }
                            // Defer visibility update to next frame
                            visibilityRafId = requestAnimationFrame(() => {
                                visibilityRafId = null;
                                updateOverlayTimelineVisibility(engine, LOGO_REVEAL_OVERLAYS, isEngineReady);
                            });
                        }
                    });

                    // Observe only the time attribute changes
                    timeObserver.observe(timeElement, {
                        attributes: true,
                        attributeFilter: [timeAttribute]
                    });

                    // Store cleanup function
                    unsubStateChanged = () => {
                        timeObserver.disconnect();
                    };
                }
            }
        }
    };

    // Track current page subscription and ID
    let unsubPages: (() => void) | null = null;
    let currentPageId = engine.scene?.getCurrentPage?.() ?? 0;

    // Update subscriptions when active page changes
    const updateForActivePage = (pageId: number) => {
        console.log('logoreveal updateForActivePage', pageId);
        currentPageId = pageId;

        //Clear overlays from previous page

        // remove stale overlays(block gone or meta removed)
        const all: number[] = findAllLogoReveals(engine);
        [...overlays.keys()].forEach((id) => {
            if (!all.includes(id)) {
                try {
                    (overlays.get(id) as any).__teardown__?.();
                } catch {
                }
                overlays.get(id)?.remove();
                overlays.delete(id);
                OVERLAY_VISIBILITY_MAP.delete(id); // Clean up visibility state
            }
        });

        // Clean up old page subscription
        if (unsubPages) {
            try { unsubPages(); } catch {}
        }

        // Subscribe to current page for Updated events to detect new logo reveals
        unsubPages = engine.event?.subscribe?.([pageId], (events: any) => {
            try {
                // Filter out events for invalid/deleted blocks using same validation as hardenTimelineApi
                const validEvents = events.filter((e: any) => {
                    try {
                        const id = e.block;
                        // Check if ID is a valid positive integer before calling engine methods
                        if (!Number.isInteger(id) || id <= 0 || id === 4294967295) {
                            return false;
                        }
                        return engine.block.isValid?.(id);
                    } catch {
                        return false;
                    }
                });

                // Check if any of the updated blocks ARE logo reveals directly
                const logoRevealBlocksInEvents = validEvents.filter((e: any) => {
                    try {
                        const hasMetadata = safeHasLogoRevealMeta(engine, e.block);
                        return hasMetadata;
                    } catch {
                        return false;
                    }
                });

                if (logoRevealBlocksInEvents.length > 0) {
                    // Logo reveal blocks were directly updated/created, rebuild immediately
                    schedule();
                    requestAnimationFrame(() => {
                        updateLogoRevealSubscriptions();
                    });
                } else {
                    // Page or other blocks updated, use delayed check in case metadata is being set
                    const hasRelevantEvent = validEvents.some((e: any) => e.type === 'Updated' || e.type === 'Created');
                    if (hasRelevantEvent) {
                        // Use double RAF to allow time for logo reveal metadata to be set
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                schedule();
                                // After rebuild, update subscriptions to include any new logo reveals
                                requestAnimationFrame(() => {
                                    updateLogoRevealSubscriptions();
                                });
                            });
                        });
                    }
                }
            } catch (error) {
                // Ignore errors from invalid blocks
                console.warn('Logo reveal page event handler error:', error);
            }
        });

        // Reposition overlays and update visibility for the new page
        requestAnimationFrame(() => {
            layoutAll();
            updateOverlayTimelineVisibility(engine, LOGO_REVEAL_OVERLAYS, isEngineReady);
        });
    };

    // Initial subscription setup
    const initialPage = engine.scene?.getCurrentPage?.();
    if (initialPage) {
        updateForActivePage(initialPage);
    }
    updateLogoRevealSubscriptions();

    // Listen to host resizes - reposition for current page
    const handleResize = () => {
        requestAnimationFrame(() => {
            layoutAll();
        });
    };

    const ro = new ResizeObserver(handleResize);
    ro.observe(host);

    // Listen to the CESDK wrapper for layout shifts (e.g. when panels open)
    const wrapper = host.closest('.cesdkWrapperStyle');
    if (wrapper) {
        ro.observe(wrapper);
    }

    // Listen to the custom panel change event
    window.addEventListener('cesdk-relayout', handleResize);

    // Listen to canvas zoom changes
    let zoomTimeout: number | null = null;
    const unsubZoomLevelChanged = engine.scene?.onZoomLevelChanged?.(() => {
        // Debounce zoom events to avoid excessive updates
        if (zoomTimeout) clearTimeout(zoomTimeout);
        zoomTimeout = window.setTimeout(() => {
            layoutAll();
        }, 50); // 50ms debounce - quick response for zoom
    });


    // Set initial visibility based on current timeline position
    updateOverlayTimelineVisibility(engine, LOGO_REVEAL_OVERLAYS, isEngineReady);

    // Return controller object
    return {
        updateForActivePage,
        cleanup: () => {
            window.removeEventListener('cesdk-relayout', handleResize);
            try {
                unsubLogoReveals?.();
            } catch {
            }
            try {
                unsubPages?.();
            } catch {
            }
            try {
                ro.disconnect();
            } catch {
            }
            try {
                unsubZoomLevelChanged?.();
            } catch {
            }
            if (zoomTimeout) clearTimeout(zoomTimeout);
            try {
                unsubStateChanged?.();
            } catch {
            }
            // Cancel any pending visibility RAF
            if (visibilityRafId !== null) {
                cancelAnimationFrame(visibilityRafId);
                visibilityRafId = null;
            }
            overlays.forEach((el) => {
                try {
                    (el as any).__teardown__?.();
                } catch {
                }
                if (el.parentElement === host) el.remove();
            });
            if (host.parentElement) host.parentElement.removeChild(host);
        }
    };
}

export function setRubMeta(engine: CreativeEngine, blockId: number, meta: RubMeta): void {
    engine.block.setMetadata?.(blockId, RUB_SCREEN_META_KEY, JSON.stringify(withDefaults(meta)));
}

/** --------------------------------------
 * Scene helpers
 * ------------------------------------- */

/**
 * Full-page logo-reveal overlay for a given block.
 * Injects a content container with the same DOM structure as slide.html:
 */
function createLogoRevealOverlay(
    engine: any,
    blockId: number,
    mountRoot: HTMLElement,
    interactive: boolean,
    opts: {
        onComplete?: (blockId: number) => void;
        /** Optional: append extra UI next to the Animate DOM */
        content?: HTMLElement | ((api: { complete: () => void }) => HTMLElement) | null;
    } = {}
): HTMLDivElement {
    // Root (positioned later by positionOverlayToBlock)
    const el = document.createElement('div');
    Object.assign(el.style, {
        position: 'absolute',
        left: '0',
        top: '0',
        right: '0',
        bottom: '0',
        zIndex: '10',
        pointerEvents: 'none', // controls if the inner content is interactive
        opacity: 1 // Always full opacity in both builder and runtime
    });
    el.dataset.idecideOverlay = 'logo-reveal';
    el.dataset.blockId = String(blockId);

    try {
        const ownerPage = findOwningPage(engine, blockId);
        el.dataset.ownerPage = ownerPage != null ? String(ownerPage) : '';
    } catch {
    }

    const content = document.createElement('content');
    content.setAttribute('data-role', 'content');
    Object.assign(content.style, {
        position: 'absolute',
        inset: '0',
        pointerEvents: interactive ? 'auto' : 'none',
        cursor: interactive ? 'default' : 'default', // Prevent grabbing cursor
        userSelect: 'none',
        WebkitUserSelect: 'none'
    });
    el.appendChild(content);

    // Create unique IDs for this overlay's elements to avoid collisions with multiple overlays
    const uniqueId = `logo-reveal-${blockId}`;

    // Create canvas scratch-off overlay container
    const coverLayers = document.createElement('div');
    coverLayers.id = `${uniqueId}-coverLayers`;
    Object.assign(coverLayers.style, {
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        zIndex: '0',
        pointerEvents: interactive ? 'auto' : 'none',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        opacity: '1',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'normal',
    });
    content.appendChild(coverLayers);

    // Create canvas element for scratch-off
    const coverCanvas = document.createElement('canvas');
    coverCanvas.id = `${uniqueId}-coverCanvas`;
    Object.assign(coverCanvas.style, {
        display: 'block',
        width: '100%',
        height: '100%'
    });
    coverLayers.appendChild(coverCanvas);

    // Create audio element for scratch sound
    const scratchAudio = document.createElement('audio');
    scratchAudio.id = `${uniqueId}-scratchAudio`;
    scratchAudio.setAttribute('preload', 'auto');
    scratchAudio.setAttribute('loop', 'true');
    coverLayers.appendChild(scratchAudio);


    // Completion helper
    const complete = () => {
        try {
            (el as any).__teardown__?.();
        } catch {
        }
        el.remove();
        opts.onComplete?.(blockId);
    };

    // Optional extra content (mounted after our Animate DOM)
    if (opts.content) {
        const node = typeof opts.content === 'function'
            ? opts.content({complete})
            : opts.content;
        if (node) content.appendChild(node);
    }

    // Allow any child to dispatch this to signal done:
    const onCustomComplete = () => complete();
    content.addEventListener('logo-reveal-complete', onCustomComplete as EventListener);

    // Teardown hook
    (el as any).__teardown__ = () => {
        content.removeEventListener('logo-reveal-complete', onCustomComplete as EventListener);
    };

    mountRoot.appendChild(el);

    // Note: Canvas scratch-off initialization happens in mountLogoRevealOverlays
    // after positionOverlayToBlock is called to ensure proper dimensions

    return el;
}


/** Scale logo-reveal overlay internals when the block (page rect) changes. */
export function updateLogoRevealGeometry(el: HTMLElement, engine?: any, blockId?: number): void {
    // Resize the canvas element dimensions to match the overlay size
    // This causes CoverScratch to scale text, icon, and background proportionally

    const overlayWidth = parseFloat(el.style.width);
    const overlayHeight = parseFloat(el.style.height);

    // Find the coverLayers div and set its dimensions
    const content = el.querySelector('[data-role="content"]') as HTMLElement;
    if (content) {
        const coverLayers = content.querySelector('[id$="-coverLayers"]') as HTMLElement;
        const coverCanvas = content.querySelector('[id$="-coverCanvas"]') as HTMLCanvasElement;

        if (coverLayers && coverCanvas && overlayWidth && overlayHeight) {
            // Update coverLayers dimensions
            coverLayers.style.width = `${overlayWidth}px`;
            coverLayers.style.height = `${overlayHeight}px`;

            // ONLY update CSS dimensions, NOT canvas attributes
            // This scales the display size while keeping the internal resolution
            coverCanvas.style.width = `${overlayWidth}px`;
            coverCanvas.style.height = `${overlayHeight}px`;
        }
    }
}

/**
 * Initialize the canvas-based scratch-off effect
 */
async function bootCanvasScratchOff(opts: {
    containerEl: HTMLElement;  // Parent container with proper dimensions
    coverLayers: HTMLElement;
    coverCanvas: HTMLElement;
    scratchAudio: HTMLElement;
    engine: any;
    blockId: number;
}): Promise<() => void> {
    // Load CSS styles
    await loadStylesheet(RUB_YOUR_SCREEN_COVER_CSS_URL);

    // Load the CoverScratch JavaScript
    await loadScriptOnce(RUB_YOUR_SCREEN_COVER_JS_URL);

    // Wait for CoverScratch to be available
    if (!(window as any).CoverScratch) {
        throw new Error('CoverScratch not available after loading script');
    }

    // Use the provided elements directly
    const {coverLayers, coverCanvas, scratchAudio, engine, blockId} = opts;

    // Get the Logo Reveal metadata to use custom text, hand color, and images
    const meta = getLogoRevealMeta(engine, blockId);
    const customText = meta.text || "Rub Your Screen";
    const handColor = meta.style?.handColor || "blue";

    // Use custom images if provided, otherwise use defaults
    const coverImageUrl = meta.images?.coverImageUrl || RUB_YOUR_SCREEN_COVER_IMAGE_URL;
    const coverIconUrl = meta.images?.coverIconUrl || RUB_YOUR_SCREEN_ICON_URL;

    // Only apply color tint to the default icon, not custom uploaded images
    const isDefaultIcon = !meta.images?.coverIconUrl;

    // Map hand color names to CSS colors
    const colorMap: Record<string, string> = {
        blue: "#4A90E2",
        red: "#E74C3C",
        green: "#2ECC71",
        white: "#FFFFFF",
        black: "#2C3E50"
    };

    // Get the actual container width to calculate responsive sizes
    // This ensures text/icon scale proportionally with zoom level
    const {containerEl} = opts;
    const actualWidth = containerEl.offsetWidth || PRESENTATION_WIDTH;
    const scaleFactor = actualWidth / PRESENTATION_WIDTH;


    // Auto-calculate font size to fit text within bounds
    // Split text into lines and calculate size for each device type
    const lines = customText.split('\n').filter(line => line.trim().length > 0);

    // Helper function to calculate font size that fits text in available width
    const calculateFontSize = (text: string, availableWidth: number, maxSize: number = 120): number => {
        if (!text || text.length === 0) return maxSize;
        // Each character takes approximately 0.7 * fontSize pixels for Poppins font weight 800
        const charWidthRatio = 0.7;
        const estimatedSize = availableWidth / (text.length * charWidthRatio);
        return Math.min(maxSize, Math.max(20, estimatedSize));
    };

    // Base sizing calculations for PRESENTATION_WIDTH, then scale for actual width
    const baseDesktopIconWidth = 150;
    const baseDesktopAvailableWidth = PRESENTATION_WIDTH - (100 * 2) - baseDesktopIconWidth - 24;
    const baseDesktopFontSizes = lines.map(line => calculateFontSize(line, baseDesktopAvailableWidth, 120));
    const baseDesktopFontSize = lines.length > 0 ? Math.min(...baseDesktopFontSizes, 120) : 120;

    // Scale everything by the actual width ratio
    const desktopIconWidth = baseDesktopIconWidth * scaleFactor;
    const desktopFontSize = baseDesktopFontSize * scaleFactor;

    // Tablet sizing (80% of desktop)
    const tabletFontSize = desktopFontSize * 0.8;
    const tabletIconWidth = desktopIconWidth * 0.8;

    // Mobile sizing (53.3% of desktop, column layout)
    const mobileFontSize = desktopFontSize * 0.533;
    const mobileIconWidth = desktopIconWidth * 0.533;

    // Configure the scratch-off settings
    const settings = {
        // Brush
        dynamicBrush: "stroke",
        brushRadiusPercent: 0.07,
        brushRadiusPx: 70,
        brushEdge: "rough" as const,
        roughBrush: {
            coreRadiusRatio: 0.2,
            rimDensity: 300,
            rimWidthRatio: 1,
            jitter: 1.0,
            alpha: 1.0,
            dotSizeMinRatio: 0.01,
            dotSizeMaxRatio: 0.3,
            skipProbability: 0.5
        },

        // Finish logic
        finishThresholdPercent: 60,

        // Text & font - USE CUSTOM TEXT FROM METADATA
        titleText: customText,
        fontFamily: "Poppins",
        fontWeight: 800,
        titleColor: "#ffffff",

        // Assets - USE CUSTOM IMAGES FROM METADATA OR DEFAULTS
        coverImageUrl: coverImageUrl,
        coverIconUrl: coverIconUrl,
        // Only apply iconColor to default icon, not custom images
        iconColor: isDefaultIcon ? (colorMap[handColor] || colorMap['blue']) : undefined,

        // Scratch SFX
        scratchAudioUrl: RUB_YOUR_SCREEN_AUDIO_URL,
        scratchVolume: 0.6,

        // Breakpoints
        breakpoints: {
            mobileMax: 600,
            tabletMax: 1024
        },
        // Auto-calculated responsive sizes based on text length
        sizes: {
            desktop: { iconWidth: desktopIconWidth, titleSize: desktopFontSize, gap: 24, margin: 100, row: true },
            tablet: { iconWidth: tabletIconWidth, titleSize: tabletFontSize, gap: 20, margin: 80, row: true },
            mobile: { iconWidth: mobileIconWidth, titleSize: mobileFontSize, gap: 16, margin: 40, row: false }
        },

        // Pulse (pre-interaction)
        pulse: { enabled: true, scaleMin: 0.98, scaleMax: 1.10, speedMs: 3000 },

        // Finish callback
        onFinished: function (api: any) {
            api.fadeOutCover(650);
            // Dispatch custom event for completion - bubbles: true so it reaches overlay-container
            coverLayers.dispatchEvent(new CustomEvent('logo-reveal-complete', { bubbles: true }));
        }
    };

    // Store settings on container element for potential reinitialization
    (containerEl as any).__scratchSettings = {
        coverLayers: coverLayers,
        coverCanvas: coverCanvas,
        scratchAudio: scratchAudio,
        settings: settings
    };

    // Verify the canvas element is actually in the document DOM
    // The CoverScratch library uses document.querySelector, so elements must be in the main DOM tree
    /*const canvasInDom = document.getElementById(coverCanvas.id);
    if (!canvasInDom) {
        console.warn('[RubScreen] Canvas element not attached to document, cannot initialize. ID:', coverCanvas.id);
        console.warn('[RubScreen] Canvas element:', coverCanvas);
        console.warn('[RubScreen] Canvas isConnected:', coverCanvas.isConnected);
        return () => {}; // Return no-op cleanup
    }*/

    // Initialize the scratch-off using the element IDs
    (window as any).CoverScratch.init(coverLayers, coverCanvas, scratchAudio, settings);

    // Trigger resize to ensure canvas matches container dimensions
    if ((window as any).CoverScratch?.resize) {
        (window as any).CoverScratch.resize();
    }

    // Disposer
    return () => {
        // Clean up if needed
    };
}

/**
 * Load a stylesheet dynamically
 */
function loadStylesheet(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
        // Check if already loaded
        const existing = document.querySelector(`link[href="${url}"]`);
        if (existing) {
            resolve();
            return;
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url;
        link.onload = () => resolve();
        link.onerror = () => reject(new Error(`Failed to load stylesheet: ${url}`));
        document.head.appendChild(link);
    });
}
